import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  Container,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Swal from 'sweetalert2';
import {
  Search,
  MapPin,
  Briefcase,
  Clock,
  Heart,
  Zap,
  GraduationCap,
  Award,
  DollarSign,
  Calendar,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Button from '../../components/Button';
import Dialog from '../../components/Dialog';
import TextInput from '../../components/TextInput';
import PageLoader from '../../components/PageLoader';
import { colors } from '../../config/colors';
import { EMAIL, testRegex } from '../../config/regex';
import { uploadResume, getJobOpenings } from '../../api';

// Styled Components
const HeaderSection = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 50%, ${colors.secondary[50]} 100%)`,
  padding: theme.spacing(8, 2),
  borderRadius: '0 0 40px 40px',
  marginBottom: theme.spacing(4),
  color: 'white',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
}));

const StyledCard = styled(Card)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  border: `1px solid ${colors.gray[200]}`,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    borderColor: colors.primary[500],
  },
});

// Static filter options (always shown) – job opportunities below are from API
const STATIC_DEPARTMENTS = ['All', 'Research & Analysis', 'Content', 'Design', 'Engineering', 'Education', 'Marketing', 'Analytics', 'Operations'];

const VERCEL_PAYLOAD_LIMIT_MB = 4.5;
const MAX_PHOTO_SIZE_MB = 20;

const Career = () => {
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDept, setSelectedDept] = useState('All');
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [resumeForm, setResumeForm] = useState({
    name: '',
    email: '',
    position: '',
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeErrors, setResumeErrors] = useState({});
  const [resumeSubmitting, setResumeSubmitting] = useState(false);
  const [resumeMessage, setResumeMessage] = useState('');
  const [resumeFileInputKey, setResumeFileInputKey] = useState(0);
  const [isPositionLocked, setIsPositionLocked] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await getJobOpenings();
        if (mounted && Array.isArray(list)) {
          setJobs(list);
        } else if (mounted) {
          setJobs([]);
        }
      } catch (_err) {
        if (mounted) setJobs([]);
      } finally {
        if (mounted) setJobsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filteredJobs = jobs.filter(job =>
    (selectedDept === 'All' || job.department === selectedDept) &&
    (job.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const benefits = [
    { icon: <DollarSign size={32} />, title: 'Competitive Salary', desc: 'Best in industry packages' },
    { icon: <Heart size={32} />, title: 'Fun Activity', desc: 'Regular Team Outings and Celebrations' },
    { icon: <GraduationCap size={32} />, title: 'Learning & Growth', desc: 'Continuous skill development' },
    { icon: <Calendar size={32} />, title: 'Flexible Work', desc: 'Work-life balance' },
    { icon: <Award size={32} />, title: 'Recognition', desc: 'Rewards & appreciation' },
    { icon: <Zap size={32} />, title: 'Fast Growth', desc: 'Rapid career advancement' },
  ];

  const validateResumeForm = () => {
    const errors = {};
    if (!resumeForm.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!resumeForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!testRegex(EMAIL, resumeForm.email)) {
      errors.email = 'Enter a valid email';
    }
    if (!resumeFile) {
      errors.file = 'Please select a resume file';
    }
    setResumeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResumeSubmit = async (e) => {
    e.preventDefault();
    if (!validateResumeForm()) return;
    try {
      setResumeSubmitting(true);
      setResumeMessage('');
      const formData = new FormData();
      formData.append('name', resumeForm.name.trim());
      formData.append('email', resumeForm.email.trim());
      if (resumeForm.position) formData.append('position', resumeForm.position.trim());
      
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const limitMb = isLocal ? MAX_PHOTO_SIZE_MB : VERCEL_PAYLOAD_LIMIT_MB;
      const maxBytes = limitMb * 1024 * 1024;
      const minBytes = 1024; // 1KB

      if (resumeFile) {
        if (resumeFile.size < minBytes) {
          throw new Error('File is too small. Minimum allowed size is 1KB.');
        }
        if (resumeFile.size > maxBytes) {
          throw new Error(`File is too large. Maximum allowed size is ${limitMb}MB.`);
        }
      }

      formData.append('resume', resumeFile);
      await uploadResume(formData);
      closeResumeModal();
      Swal.fire({
        icon: 'success',
        title: 'Resume submitted',
        text: 'Your resume has been uploaded successfully.',
        confirmButtonColor: colors.primary[500],
      });
    } catch (err) {
      setResumeMessage(err.response?.data?.message || err.message || 'Failed to upload resume. Please try again.');
    } finally {
      setResumeSubmitting(false);
    }
  };

  const openResumeModal = (position = '') => {
    setResumeForm({
      name: '',
      email: '',
      position,
    });
    setIsPositionLocked(Boolean(position));
    setResumeFile(null);
    setResumeErrors({});
    setResumeMessage('');
    setResumeModalOpen(true);
    setResumeFileInputKey((prev) => prev + 1);
  };

  const closeResumeModal = () => {
    setResumeModalOpen(false);
    setResumeForm({
      name: '',
      email: '',
      position: '',
    });
    setIsPositionLocked(false);
    setResumeFile(null);
    setResumeErrors({});
    setResumeMessage('');
    setResumeFileInputKey((prev) => prev + 1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        <HeaderSection elevation={0}>
          <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4">
            <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2, fontSize: { xs: '2rem', md: '3.5rem' } }} className="4xl:!text-8xl">
              Careers at Market Mavericks
            </Typography>
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }} className="4xl:!max-w-4xl">
              <TextField
                fullWidth
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ bgcolor: 'white', borderRadius: '16px' }}
                className="4xl:scale-125 4xl:mt-8"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search size={20} color={colors.primary[500]} /></InputAdornment>,
                }}
              />
            </Box>
          </div>
        </HeaderSection>

        <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 py-4">
          {/* Job roles (static) */}
          <Tabs 
            value={selectedDept} 
            onChange={(e, v) => setSelectedDept(v)} 
            variant="scrollable" 
            sx={{ mb: 4, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .Mui-selected': { color: colors.primary[600] }, '& .MuiTabs-indicator': { backgroundColor: colors.primary[500] } }}
          >
            {STATIC_DEPARTMENTS.map(dept => <Tab key={dept} label={dept} value={dept} />)}
          </Tabs>

          {/* Job Grid with 3 columns */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', // Desktop: 3 Columns
            gap: '24px',
          }} className="responsive-job-grid">
            
            <style dangerouslySetInnerHTML={{ __html: `
              @media (min-width: 2540px) { .responsive-job-grid { grid-template-columns: repeat(4, 1fr) !important; } }
              @media (max-width: 1024px) { .responsive-job-grid { grid-template-columns: repeat(2, 1fr) !important; } }
              @media (max-width: 640px) { .responsive-job-grid { grid-template-columns: 1fr !important; } }
            `}} />

            {jobsLoading ? (
              <PageLoader />
            ) : jobs.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem' }}>
                <Typography color="text.secondary">Jobs are not available.</Typography>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem' }}>
                <Typography color="text.secondary">No jobs match your filters.</Typography>
              </div>
            ) : (
              filteredJobs.map((job) => (
              <StyledCard key={job.id}>
                <Box sx={{ p: 3, bgcolor: colors.primary[200] + '44' }}>
                  <Chip label={job.department} size="small" sx={{ bgcolor: colors.primary[500], color: 'white', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold', height: '60px', overflow: 'hidden', color: colors.primary[700] }}>
                    {job.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ height: '40px', overflow: 'hidden' }}>
                    {job.description}
                  </Typography>
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MapPin size={16} color={colors.primary[500]} /> <Typography variant="body2">{job.location}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Briefcase size={16} color={colors.secondary[500]} /> <Typography variant="body2">{job.type || 'Full-time'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Clock size={16} color={colors.primary[500]} /> <Typography variant="body2">{job.experience}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DollarSign size={16} color={colors.primary[600]} /> <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{job.salary}</Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(Array.isArray(job.skills) ? job.skills : []).map(skill => (
                      <Chip key={skill} label={skill} size="small" sx={{ fontSize: '10px', height: 20 }} />
                    ))}
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button 
                    variant="primary" 
                    className="w-full"
                    onClick={() => openResumeModal(job.title)}
                  >
                    Apply Now
                  </Button>
                </CardActions>
              </StyledCard>
            ))
            )}
          </div>
        </div>
          {/* Why Join Us Section (Original Design) */}
          <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 mt-20 mb-12">
  <div className="text-center mb-12">
    <h3 className={`text-3xl md:text-4xl font-bold mb-3`} style={{ color: colors.primary[700] }}>
      Why Join Market Mavericks?
    </h3>
    <p className="text-gray-600 max-w-2xl mx-auto">
      We offer more than just a job. Join a team that's shaping the future of financial education.
    </p>
  </div>

  {/* Responsive Container: Mobile pe scroll, Desktop pe 6 columns */}
  <div className="flex md:grid md:grid-cols-6 gap-4 overflow-x-auto md:overflow-visible pb-6 scrollbar-hide">
    
    {/* Custom CSS to hide scrollbar while keeping functionality */}
    <style dangerouslySetInnerHTML={{ __html: `
      .scrollbar-hide::-webkit-scrollbar { display: none; }
      .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
    `}} />

    {benefits.map((benefit, index) => (
      <div 
        key={index}
        className="flex-none w-[260px] md:w-full bg-white border border-gray-100 rounded-[24px] p-6 
                   flex flex-col items-center text-center transition-all duration-300 
                   hover:-translate-y-2 hover:shadow-xl hover:border-green-400 group"
        style={{ minHeight: '220px' }}
      >
        {/* Icon Container - Perfect Centering */}
        <div 
          className="w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors duration-300"
          style={{ backgroundColor: `${colors.primary[50]}` }}
        >
          <div style={{ color: colors.primary[500] }} className="group-hover:scale-110 transition-transform">
            {benefit.icon}
          </div>
        </div>

        <h4 className="font-bold text-gray-800 mb-2 text-sm lg:text-base leading-tight">
          {benefit.title}
        </h4>
        
        <p className="text-gray-500 text-xs lg:text-sm leading-relaxed">
          {benefit.desc}
        </p>
      </div>
    ))}
  </div>
</div>

          {/* CTA Section (Original Design) */}
          <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 mb-8">
            <Paper sx={{ background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.secondary[500]})`, p: 6, borderRadius: '30px', textAlign: 'center', color: 'white' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>Don't see a role that fits?</Typography>
            <Typography sx={{ mb: 4, opacity: 0.9 }}>Send us your resume and we'll keep you in mind for future opportunities!</Typography>
            <Button
              variant="secondary"
              size="lg"
              className="!bg-white !text-primary-600"
              onClick={() => openResumeModal()}
            >
              Send Resume
            </Button>
            </Paper>
          </div>
      </main>

      <Footer />

      <Dialog
        open={resumeModalOpen}
        onClose={closeResumeModal}
        title="Send your resume"
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={closeResumeModal}
              disabled={resumeSubmitting}
            >
              Close
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleResumeSubmit}
              disabled={resumeSubmitting}
              className='whitespace-nowrap'
            >
              {resumeSubmitting ? 'Uploading...' : 'Upload Resume'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleResumeSubmit} className="space-y-4">
          <TextInput
            label="Full name"
            name="name"
            value={resumeForm.name}
            onChange={(e) => setResumeForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Your full name"
            required
            error={resumeErrors.name}
          />
          <TextInput
            label="Email"
            name="email"
            type="email"
            value={resumeForm.email}
            onChange={(e) => {
              const val = e.target.value;
              setResumeForm((prev) => ({ ...prev, email: val }));
              if (!val.trim()) {
                setResumeErrors((prev) => ({ ...prev, email: 'Email is required' }));
              } else if (!testRegex(EMAIL, val)) {
                setResumeErrors((prev) => ({ ...prev, email: 'Enter a valid email address' }));
              } else {
                setResumeErrors((prev) => ({ ...prev, email: '' }));
              }
            }}
            placeholder="you@example.com"
            required
            error={resumeErrors.email}
          />
          <TextInput
            label="Preferred role"
            name="position"
            disabled={isPositionLocked}
            value={resumeForm.position}
            onChange={(e) => setResumeForm((prev) => ({ ...prev, position: e.target.value }))}
            placeholder="e.g. Content Writer, Designer"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resume file (PDF or image)
            </label>
            <input
              key={resumeFileInputKey}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setResumeFile(file);
                setResumeErrors((prev) => ({ ...prev, file: file ? '' : prev.file }));
              }}
              className="block w-full text-sm text-gray-700"
            />
            {resumeErrors.file && (
              <p className="text-xs text-red-600 mt-1">{resumeErrors.file}</p>
            )}
          </div>
          {resumeMessage && (
            <p className="text-sm mt-2 text-gray-700">{resumeMessage}</p>
          )}
        </form>
      </Dialog>
    </div>
  );
};

export default Career;